import {
  COMPANY_ADDRESS_CITY,
  COMPANY_ADDRESS_FULL,
  COMPANY_ADDRESS_STREET,
  COMPANY_INITIALS,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_REGION,
  COMPANY_SHORT,
  HERO_IMAGE_SOURCE_URL,
  HERO_IMAGE_URL,
  MAP_URL,
  FOCUS_AREAS,
  PROFILE_FACTS,
  PUBLIC_INFO,
} from './constants';

function Header() {
  const navItems = [
    { label: 'Profile', href: '#profile' },
    { label: 'Focus', href: '#focus' },
    { label: 'Directory', href: '#directory' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-line/70 bg-cream/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-forest/30 bg-forest text-xs font-black tracking-wider text-sand">
            {COMPANY_INITIALS}
          </span>
          <span className="hidden flex-col leading-tight sm:flex">
            <span className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">
              {COMPANY_REGION}
            </span>
            <span className="truncate text-sm font-black tracking-wide text-forest">{COMPANY_SHORT}</span>
          </span>
        </a>
        <nav className="hidden items-center gap-7 text-sm font-semibold text-bark/70 md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-terracotta">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          className="rounded-full border border-forest/30 bg-forest px-4 py-2 text-sm font-bold text-sand transition hover:border-terracotta hover:bg-terracotta"
        >
          View Address
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-forest text-sand">
      <img
        src={HERO_IMAGE_URL}
        alt="Southern Ohio residential exterior"
        className="absolute inset-0 -z-10 h-full w-full object-cover opacity-70"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-forest/85 via-forest/55 to-bark/90" />

      <div className="mx-auto max-w-7xl px-5 pb-28 pt-20 sm:pt-24 lg:pb-36 lg:pt-32">
        <p className="text-xs font-black uppercase tracking-[0.3em] text-terracotta">
          {COMPANY_REGION} Residences
        </p>
        <h1 className="mt-6 max-w-4xl font-serif text-5xl font-black leading-[1.05] tracking-tight text-sand sm:text-6xl lg:text-7xl">
          {COMPANY_NAME}
        </h1>
        <p className="mt-7 max-w-2xl text-lg leading-8 text-sand/85">
          A measured public profile for a residential organization headquartered along Nimitzview Drive in
          Cincinnati, Ohio. This page is presented for directory and correspondence reference only.
        </p>

        <div className="mt-12 grid max-w-5xl gap-4 sm:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-2xl border border-sand/15 bg-bark/55 p-7 shadow-lift backdrop-blur">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">Mailing Address</p>
            <p className="mt-3 font-serif text-2xl font-black leading-snug text-sand">
              {COMPANY_ADDRESS_STREET}
            </p>
            <p className="mt-1 text-sm font-semibold text-sand/75">{COMPANY_ADDRESS_CITY}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#contact"
                className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-sand transition hover:bg-clay"
              >
                Open contact
              </a>
              <a
                href={MAP_URL}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-sand/40 px-5 py-2.5 text-sm font-bold text-sand transition hover:border-terracotta hover:text-terracotta"
              >
                View on map
              </a>
            </div>
          </div>
          <div className="flex flex-col justify-between rounded-2xl border border-sand/15 bg-sand/10 p-7 backdrop-blur">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-sand/70">Profile Type</p>
            <p className="mt-3 font-serif text-xl leading-snug text-sand">
              Public-safe directory entry for a Southern Ohio residential organization.
            </p>
            <a
              href={HERO_IMAGE_SOURCE_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex w-fit text-xs font-bold uppercase tracking-[0.2em] text-terracotta underline-offset-4 hover:underline"
            >
              Image source
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FactsBento() {
  return (
    <section aria-label="Profile facts" className="bg-cream">
      <div className="mx-auto max-w-7xl px-5 py-16">
        <div className="grid gap-4 md:grid-cols-6 md:grid-rows-2">
          {PROFILE_FACTS.map((fact, idx) => {
            const spans = [
              'md:col-span-3 md:row-span-2',
              'md:col-span-3',
              'md:col-span-2',
              'md:col-span-1',
            ];
            const tones = [
              'bg-forest text-sand border-forest',
              'bg-sand text-bark border-line',
              'bg-terracotta/95 text-sand border-clay',
              'bg-cream text-bark border-line',
            ];
            return (
              <div
                key={fact.label}
                className={`flex flex-col justify-between rounded-2xl border p-7 ${spans[idx]} ${tones[idx]}`}
              >
                <p className="text-[11px] font-black uppercase tracking-[0.24em] opacity-80">{fact.label}</p>
                <p className={`mt-6 font-serif font-black leading-tight ${idx === 0 ? 'text-4xl sm:text-5xl' : 'text-2xl'}`}>
                  {fact.value}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProfileSection() {
  return (
    <section id="profile" className="bg-sand">
      <div className="mx-auto max-w-7xl px-5 py-20">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">Profile</p>
            <h2 className="mt-3 font-serif text-4xl font-black leading-tight text-forest sm:text-5xl">
              A restrained portrait of a Southern Ohio residential organization.
            </h2>
          </div>
          <div className="space-y-5 text-bark/85">
            <p className="font-serif text-2xl leading-snug text-forest">
              Headquartered in the Anderson Township area of Cincinnati, the organization keeps a deliberately
              quiet public footprint.
            </p>
            <p className="leading-8">
              The supplied mailing suite on Nimitzview Drive serves as the formal point of correspondence. This
              page restates only that public reference data — street, suite, and city — without surfacing private
              registry identifiers.
            </p>
            <p className="leading-8">
              The visual treatment leans on a residential palette — forest, terracotta, sand — paired with an
              editorial type system, in place of generic corporate marketing language.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-forest/20 bg-cream px-5 py-3 text-sm font-bold text-forest">
              <span className="h-2 w-2 rounded-full bg-terracotta" aria-hidden />
              Public directory entry only
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FocusTimeline() {
  return (
    <section id="focus" className="bg-cream">
      <div className="mx-auto max-w-7xl px-5 py-20">
        <div className="flex flex-col gap-6 border-b border-line pb-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">Editorial Notes</p>
            <h2 className="mt-3 font-serif text-4xl font-black leading-tight text-forest sm:text-5xl">
              How the profile is composed.
            </h2>
            <p className="mt-4 leading-7 text-bark/75">
              A vertical timeline replaces generic feature cards — each note explains a choice behind the
              residential profile layout.
            </p>
          </div>
          <p className="max-w-sm text-xs font-bold uppercase tracking-[0.24em] text-bark/55">
            Residential · Conservative · Source-backed
          </p>
        </div>

        <div className="relative mt-12">
          <span
            className="absolute left-[19px] top-2 bottom-2 w-px bg-line md:left-[27px]"
            aria-hidden
          />
          <div className="space-y-10">
            {FOCUS_AREAS.map((note) => (
              <article key={note.title} className="relative grid gap-5 pl-12 md:grid-cols-[1fr_1.6fr] md:pl-20">
                <span
                  className="absolute left-2 top-1 grid h-9 w-9 place-items-center rounded-full border-2 border-cream bg-forest font-black text-sand md:left-2 md:h-12 md:w-12 md:text-lg"
                  aria-hidden
                >
                  {note.number}
                </span>
                <h3 className="font-serif text-2xl font-black leading-snug text-forest">{note.title}</h3>
                <p className="leading-7 text-bark/80">{note.body}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function DirectoryBand() {
  return (
    <section id="directory" className="bg-forest text-sand">
      <div className="mx-auto max-w-7xl px-5 py-20">
        <div className="flex flex-col gap-6 border-b border-sand/15 pb-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">Directory</p>
            <h2 className="mt-3 font-serif text-4xl font-black leading-tight sm:text-5xl">
              Public reference data.
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-7 text-sand/70">
            Only source-backed organization and address fields appear here. Private registry numbers are
            intentionally omitted.
          </p>
        </div>
        <dl className="mt-10 grid gap-px overflow-hidden rounded-2xl bg-sand/10 sm:grid-cols-2 lg:grid-cols-4">
          {PUBLIC_INFO.map((item) => (
            <div key={item.label} className="flex flex-col gap-3 bg-forest p-6">
              <dt className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">
                {item.label}
              </dt>
              <dd className="font-serif text-xl font-black leading-snug text-sand">{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function ContactBand() {
  return (
    <section id="contact" className="bg-sand">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-[1fr_1.1fr] lg:items-center">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">Contact</p>
          <h2 className="mt-3 font-serif text-4xl font-black leading-tight text-forest sm:text-5xl">
            {COMPANY_LOCATION}
          </h2>
          <p className="mt-5 max-w-xl leading-7 text-bark/80">
            Use the supplied address below for public profile, directory, and correspondence reference. No
            additional contact channels are published on this page.
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-3">
            <a
              href={MAP_URL}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-terracotta px-6 py-3 font-bold text-sand transition hover:bg-clay"
            >
              Open map
            </a>
            <a
              href="#top"
              className="rounded-full border border-forest/30 px-6 py-3 font-bold text-forest transition hover:border-terracotta hover:text-terracotta"
            >
              Back to top
            </a>
          </div>
        </div>
        <div className="rounded-3xl border border-line bg-cream p-8 shadow-soft">
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-terracotta">Organization Address</p>
          <p className="mt-4 font-serif text-3xl font-black leading-tight text-forest">{COMPANY_ADDRESS_FULL}</p>
          <div className="mt-8 grid grid-cols-2 gap-4 border-t border-line pt-6 text-sm">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-bark/55">Street</p>
              <p className="mt-2 font-bold text-forest">{COMPANY_ADDRESS_STREET}</p>
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-bark/55">City / ZIP</p>
              <p className="mt-2 font-bold text-forest">{COMPANY_ADDRESS_CITY}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-bark text-sand/70">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-10 text-sm md:flex-row md:items-center md:justify-between">
        <p className="font-bold text-sand">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <p>{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-cream text-bark">
      <a
        href="#profile"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-terracotta focus:px-3 focus:py-2 focus:text-sand"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <FactsBento />
        <ProfileSection />
        <FocusTimeline />
        <DirectoryBand />
        <ContactBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
